import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, ShieldCheck } from "lucide-react";
import { CONSENT } from "@/data/quiz";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function EmailCapture({ onSubmit, submitting, privacyUrl }) {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [choice, setChoice] = useState("");
  const [errors, setErrors] = useState({});

  const handleSubmit = (e) => {
    e.preventDefault();
    const next = {};
    if (!EMAIL_RE.test(email.trim())) next.email = "Please enter a valid email address.";
    if (!choice) next.choice = "Please choose one option so we know your preference.";
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    onSubmit({ firstName: firstName.trim(), email: email.trim().toLowerCase(), choice });
  };

  return (
    <main className="max-w-2xl mx-auto px-4 sm:px-6 py-10 sm:py-16" data-testid="email-capture">
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight leading-tight text-[#1e292b]">
          Your next chapter starts with understanding what matters.
        </h2>
        <p className="mt-4 text-lg leading-relaxed text-[#5a686b]">
          Enter your email to see your personalised results. We’ll also email you a copy, together with
          our Personal Clarity Prospectus, so you can explore the programme in your own time.
        </p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-6" noValidate>
          <div>
            <label htmlFor="first-name" className="block text-sm font-medium text-[#1e292b] mb-1.5">
              First name <span className="text-[#5a686b] font-normal">(optional)</span>
            </label>
            <input
              id="first-name"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              data-testid="first-name-input"
              className="w-full rounded-xl border-2 border-[#e2ded7] bg-white px-4 py-3 text-base text-[#1e292b] focus:border-[#4aa0a4] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4aa0a4]/25 transition-colors"
              placeholder="Your first name"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-[#1e292b] mb-1.5">
              Email address <span className="text-[#e93e36]">*</span>
            </label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? "email-error" : undefined}
              data-testid="email-input"
              className={`w-full rounded-xl border-2 bg-white px-4 py-3 text-base text-[#1e292b] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4aa0a4]/25 transition-colors ${
                errors.email ? "border-[#e93e36]" : "border-[#e2ded7] focus:border-[#4aa0a4]"
              }`}
              placeholder="you@example.com"
            />
            {errors.email && (
              <p id="email-error" className="mt-1.5 text-sm text-[#e93e36]" data-testid="email-error">
                {errors.email}
              </p>
            )}
          </div>

          <fieldset className="rounded-2xl border border-[#e2ded7] bg-white p-5 sm:p-6">
            <legend className="px-1 text-base font-medium text-[#1e292b]">
              Would you like to hear more from The Weave?
            </legend>
            <div className="mt-4 space-y-3">
              {["yes", "no"].map((key) => {
                const active = choice === key;
                return (
                  <label
                    key={key}
                    data-testid={`consent-option-${key}`}
                    className={`flex items-start gap-3 rounded-xl border-2 p-4 cursor-pointer transition-all ${
                      active ? "border-[#4aa0a4] bg-[#eef6f6]" : "border-[#e2ded7] hover:border-[#4aa0a4]/60"
                    }`}
                  >
                    <input
                      type="radio"
                      name="marketing-consent"
                      value={key}
                      checked={active}
                      onChange={() => {
                        setChoice(key);
                        setErrors((p) => ({ ...p, choice: undefined }));
                      }}
                      data-testid={`consent-radio-${key}`}
                      className="mt-1 h-4 w-4 accent-[#4aa0a4]"
                    />
                    <span>
                      <span className="block text-base font-medium text-[#1e292b]">{CONSENT[key].title}</span>
                      <span className="block text-sm leading-relaxed text-[#5a686b] mt-0.5">
                        {CONSENT[key].detail}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
            {errors.choice && (
              <p className="mt-3 text-sm text-[#e93e36]" data-testid="consent-error">
                {errors.choice}
              </p>
            )}
          </fieldset>

          <p className="text-sm leading-relaxed text-[#5a686b]">
            Your choice about future emails won’t affect your results. If you opt in, you can unsubscribe
            at any time.
            {privacyUrl && (
              <>
                {" "}
                <a
                  href={privacyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#4aa0a4] underline underline-offset-2 hover:text-[#3b868a]"
                  data-testid="privacy-link"
                >
                  Read our Privacy Notice
                </a>
                .
              </>
            )}
          </p>

          <button
            type="submit"
            disabled={submitting}
            data-testid="email-submit-button"
            className="inline-flex items-center justify-center gap-2 bg-[#4aa0a4] text-white text-base font-medium px-7 py-3.5 rounded-full hover:bg-[#3b868a] transition-all shadow-md hover:shadow-lg active:scale-[0.98] disabled:opacity-50 weave-focus"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Show my results and email my prospectus
          </button>

          <p className="flex items-center gap-2 text-xs text-[#5a686b]">
            <ShieldCheck className="h-4 w-4 text-[#4aa0a4]" />
            We’ll only use your details to send your results and, if you opt in, occasional insights.
          </p>
        </form>
      </motion.div>
    </main>
  );
}
